import { useCallback } from "react";
import { KycStatus, MaritalStatus, WorkingPermitType } from "../__generated__/graphql";
import { UserFieldsFragment } from "../__generated__/operations/graphql";

export const getWorkerProfileIsComplete = (user?: UserFieldsFragment | null) => {
    const kycVerified = user?.kycStatus?.status === KycStatus.Accepted;
    const maritalStatus = user?.workerProfile?.maritalStatus;

    const avsNumber = user?.workerProfile?.avsNumber;

    const children = user?.workerProfile?.children || [];

    const bankAccount = user?.workerProfile?.bankAccount;

    const workPermit = user?.workerProfile?.lastWorkPermit;

    const hasChildren = user?.workerProfile?.hasChildren;

    const partner = user?.workerProfile?.partner;

    const curriculumVitae = user?.workerProfile?.curriculumVitae;

    const isMarried = maritalStatus === MaritalStatus.Married;

    const address = user?.address;

    const nationality = user?.nationality;

    const isPartnerAndChildrenDataRequired =
        nationality !== "CH" &&
        workPermit?.type &&
        [WorkingPermitType.PermitB, WorkingPermitType.PermitL, WorkingPermitType.PermitS].includes(
            workPermit.type,
        );

    const isFiscalAttestationRequired = workPermit?.type === WorkingPermitType.PermitG;

    const hasFiscalAttestation = user?.workerProfile?.hasFiscalAttestation;

    const email = user?.email;

    const emailVerified = user?.verified;

    const missingInformations: string[] = [];

    if (!kycVerified) {
        missingInformations.push("kyc");
    }
    if (!bankAccount) {
        missingInformations.push("bank_account");
    }
    if (isPartnerAndChildrenDataRequired && hasChildren === null) {
        missingInformations.push("children");
    }
    if (!maritalStatus || (isPartnerAndChildrenDataRequired && isMarried && !partner)) {
        // AVS is no longer part of this check: the civil_state form cannot be saved
        // without maritalStatus, and it now allows an empty AVS (foreigner exemption or
        // the "no AVS number" toggle), so a set maritalStatus implies the AVS rule passed.
        missingInformations.push("civil_state");
    }
    if (isFiscalAttestationRequired && hasFiscalAttestation === null) {
        missingInformations.push("fiscal_attestation");
    }
    if (!email || !emailVerified) {
        // Setting the address and verifying it are a single step: an unset address cannot be
        // verified, and PostOnboardingPrepare renders one bullet per key, so splitting them would
        // list the same step twice.
        //
        // A verified address completes the step even when it is also the business email of one of
        // the user's employer profiles: the worker profile is not required to use a distinct
        // address.
        missingInformations.push("email");
    }
    if (!address) {
        missingInformations.push("home_address");
    }
    if (!curriculumVitae) {
        missingInformations.push("experiences");
    }

    return {
        isValid: missingInformations.length === 0,
        missingInformations,
        maritalStatus,
        bankAccount,
        children,
        hasChildren,
        workPermit,
        isPartnerAndChildrenDataRequired,
        isFiscalAttestationRequired,
        hasFiscalAttestation,
        email,
        emailVerified,
        address,
        avsNumber,
        curriculumVitae,
        kycVerified,
    };
};

export const useGetIsWorkerProfileComplete = () => {
    return useCallback(
        (...args: Parameters<typeof getWorkerProfileIsComplete>) =>
            getWorkerProfileIsComplete(...args),
        [],
    );
};
